import React, { useState } from "react";
import {
  Plus, Edit, Trash2, Eye, Gift, Share2, FormInput,
  Calendar, Globe, FileText
} from "lucide-react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/badge";

interface Form {
  id: number;
  name: string;
  category: string;
  type: string;
  status: string;
  lastModified: string;
  region: string;
  fields: Array<{
    id: string;
    label: string;
    type: string;
    required?: boolean;
    options?: string[];
    multiple?: boolean;
    accept?: string;
  }>;
  pricing: {
    quotePrice: { standard: number; emergency: number };
    commission: { standard: string; emergency: string };
  };
  settings: {
    autoAssign: boolean;
    requireVerification: boolean;
    allowMultipleQuotes: boolean;
    maxQuotes: number;
    autoPromote: boolean;
    promotionCount: number;
  };
  stats: {
    submissions: number;
    conversionRate: string;
    avgResponseTime: string;
    purchasedCount: number;
    hiredCount: number;
  };
}

interface FormsTabProps {
  forms: Form[];
  selectedCategory: string;
  searchQuery: string;
  onShowAddModal: () => void;
  onShowFormEditor: (form: Form) => void;
  onShowPromotionModal: (form: Form) => void;
}

const FormsTab: React.FC<FormsTabProps> = ({
  forms,
  selectedCategory,
  searchQuery,
  onShowAddModal,
  onShowFormEditor,
  onShowPromotionModal
}) => {
  const FormCard = ({ form }: { form: Form }) => (
    <Card className="p-4 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="w-full">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base sm:text-lg font-semibold">{form.name}</h3>
            {form.settings.autoPromote && (
              <Badge variant={undefined} className="bg-purple-100 text-purple-700 text-xs sm:text-sm">
                Auto-Promote ({form.settings.promotionCount})
              </Badge>
            )}
            <Badge variant={undefined} className={`text-xs sm:text-sm ${form.status === "active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {form.status.charAt(0).toUpperCase() + form.status.slice(1)}
            </Badge>
            <Badge variant="outline" className="text-xs sm:text-sm">{form.category}</Badge>
            <Badge variant={undefined} className={`text-xs sm:text-sm ${form.type === "emergency" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
              {form.type.charAt(0).toUpperCase() + form.type.slice(1)}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs sm:text-sm text-gray-500">
            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" /> Last modified: {form.lastModified}
            <Globe className="h-3 w-3 sm:h-4 sm:w-4 ml-2" /> Region: {form.region}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onShowFormEditor(form)}
          >
            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onShowFormEditor(form)}
          >
            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
          >
            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6">
        <h4 className="font-medium mb-3 text-sm sm:text-base">Form Fields</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {form.fields.map((field, index) => (
            <div key={index} className="p-2 sm:p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-xs sm:text-sm">{field.label}</span>
                {field.required && <Badge variant={undefined} className="bg-red-50 text-red-700 text-xs">Required</Badge>}
              </div>
              <span className="text-xs text-gray-500 mt-1 block">Type: {field.type}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <h4 className="font-medium mb-3 text-sm sm:text-base">Quote Pricing</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
              <span>Standard Quote</span>
              <Badge variant="outline">${form.pricing.quotePrice.standard}</Badge>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
              <span>Emergency Quote</span>
              <Badge variant="outline">${form.pricing.quotePrice.emergency}</Badge>
            </div>
          </div>
        </div>
        <div>
          <h4 className="font-medium mb-3 text-sm sm:text-base">Commission Rates</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
              <span>Standard Rate</span>
              <Badge variant="outline">{form.pricing.commission.standard}</Badge>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
              <span>Emergency Rate</span>
              <Badge variant="outline">{form.pricing.commission.emergency}</Badge>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6">
        <h4 className="font-medium mb-3 text-sm sm:text-base">Form Settings</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
            <span>Auto-assign Professionals</span>
            <Badge variant={undefined} className={form.settings.autoAssign ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {form.settings.autoAssign ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
            <span>Require Verification</span>
            <Badge variant={undefined} className={form.settings.requireVerification ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {form.settings.requireVerification ? "Yes" : "No"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
            <span>Multiple Quotes</span>
            <Badge variant={undefined} className={form.settings.allowMultipleQuotes ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
              {form.settings.allowMultipleQuotes ? "Allowed" : "Not Allowed"}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs sm:text-sm">
            <span>Max Quotes</span>
            <Badge variant="outline">{form.settings.maxQuotes}</Badge>
          </div>
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4">
          <div className="text-center">
            <div className="text-xs sm:text-sm text-gray-500">Total Submissions</div>
            <div className="font-semibold mt-1 text-sm sm:text-base">{form.stats.submissions}</div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-gray-500">Conversion Rate</div>
            <div className="font-semibold mt-1 text-sm sm:text-base">{form.stats.conversionRate}</div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-gray-500">Avg. Response Time</div>
            <div className="font-semibold mt-1 text-sm sm:text-base">{form.stats.avgResponseTime}</div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-gray-500">Purchased</div>
            <div className="font-semibold mt-1 text-sm sm:text-base">{form.stats.purchasedCount}</div>
          </div>
          <div className="text-center">
            <div className="text-xs sm:text-sm text-gray-500">Hired</div>
            <div className="font-semibold mt-1 text-sm sm:text-base">{form.stats.hiredCount}</div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t flex flex-col sm:flex-row justify-end gap-2">
        <Button 
          variant="outline" 
          className="w-full sm:w-auto flex items-center gap-2 text-sm" 
          onClick={() => onShowPromotionModal(form)}
        >
          <Gift className="h-3 w-3 sm:h-4 sm:w-4" /> Configure Auto-Promotion
        </Button>
        <Button className="w-full sm:w-auto flex items-center gap-2 text-sm">
          <Share2 className="h-3 w-3 sm:h-4 sm:w-4" /> Promote Forms
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Forms</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage service category forms and registrations</p>
        </div>
        <Button className="w-full sm:w-auto flex items-center gap-2 text-sm" onClick={onShowAddModal}>
          <Plus className="h-3 w-3 sm:h-4 sm:w-4" /> New Form
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Total Forms</p>
              <p className="text-lg sm:text-2xl font-semibold mt-1">12</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <FormInput className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Categories</p>
              <p className="text-lg sm:text-2xl font-semibold mt-1">8</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Auto-synced</p>
              <p className="text-lg sm:text-2xl font-semibold mt-1">5</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Submissions</p>
              <p className="text-lg sm:text-2xl font-semibold mt-1">1.2k</p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
            </div>
          </div>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {forms
          .filter(form => selectedCategory === "all" || form.category.toLowerCase() === selectedCategory)
          .filter(form => form.name.toLowerCase().includes(searchQuery.toLowerCase()) || form.category.toLowerCase().includes(searchQuery.toLowerCase()))
          .map(form => <FormCard key={form.id} form={form} />)}
      </div>
    </div>
  );
};

export default FormsTab;
